import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ChevronDown,
  FolderOpen,
  FolderPlus,
  TrendingUp,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import {
  Permission,
  romaniaDateKey,
  type ProjectListItem,
  type ProjectStatusValue,
} from '@kreart/shared';
import { useAuth } from '@/features/auth/useAuth';
import { useProjects } from '@/features/projects/hooks';
import { projectStatusBadgeClass } from '@/features/projects/statusBadge';
import { projectStatusLabel } from '@/features/projects/statusLabel';
import { useAnalyticsSummary } from '@/features/analytics/hooks';
import type { ProjectsListSearch } from '@/features/projects/search';
import { useUsers } from '@/features/users/hooks';
import { EmptyState, ErrorState } from '@/components/feedback';
import { clickableRowProps } from '@/lib/clickableRow';
import { ColumnOrderMenu } from '@/components/table/ColumnOrderMenu';
import { usePersistedColumnOrder } from '@/components/table/usePersistedColumnOrder';

const ACTIVE_PROJECT_STATUSES: ProjectStatusValue[] = [
  'IN_PROGRESS',
  'ON_HOLD',
  'SENT_TO_CLIENT',
  'REOFFER',
];

const RANGE_OPTIONS = [30] as const;
type DashboardRangeDays = (typeof RANGE_OPTIONS)[number];

interface StatCardProps {
  id: string;
  label: string;
  value: string | number;
  icon: LucideIcon;
  loading?: boolean;
  search?: ProjectsListSearch;
  disabled?: boolean;
}

interface RecentProjectColumn {
  key: string;
  label: string;
  className?: string;
  headerClassName?: string;
  render: (project: ProjectListItem) => ReactNode;
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canListProjects = user?.permissions.includes(Permission.PROJECTS_LIST) ?? false;
  const canCreateProjects = user?.permissions.includes(Permission.PROJECTS_CREATE) ?? false;
  const canCreateClients = user?.permissions.includes(Permission.CLIENTS_CREATE) ?? false;
  const canListUsers = user?.permissions.includes(Permission.USERS_LIST) ?? false;
  const [rangeDays, setRangeDays] = useState<DashboardRangeDays>(30);

  const summaryQ = useAnalyticsSummary();
  const recentQ = useProjects(
    { view: 'ACTIVE', page: 1, pageSize: 5, includeDeleted: false },
    canListProjects,
  );
  const upcomingDeadlinesQ = useProjects(
    { view: 'ACTIVE', page: 1, pageSize: 1, upcomingDeadline: true, includeDeleted: false },
    canListProjects,
  );
  // Romania's month: just past midnight on the 1st it is already the new one.
  const today = romaniaDateKey(Date.now());
  const currentYear = Number(today.slice(0, 4));
  const currentMonth = Number(today.slice(5, 7));
  const completedThisMonthQ = useProjects(
    {
      view: 'ACTIVE',
      page: 1,
      pageSize: 1,
      status: ['ACCEPTED'],
      year: [currentYear],
      month: [currentMonth],
      includeDeleted: false,
    },
    canListProjects,
  );
  const usersQuery = useUsers(
    { page: 1, pageSize: 100, active: true, includeDeleted: false },
    canListUsers,
  );

  const recent = canListProjects ? (recentQ.data?.items ?? []) : [];
  const summary = summaryQ.data;
  const activeProjectCount = summary
    ? ACTIVE_PROJECT_STATUSES.reduce((sum, status) => sum + summary.projectsByStatus[status], 0)
    : 0;
  const userNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const item of usersQuery.data?.items ?? []) names.set(item.id, item.fullName);
    return names;
  }, [usersQuery.data]);
  const roomCountLabel = (count: number) =>
    t('projects.columns.roomCountValue', {
      count,
      defaultValue: `${count} cameră(e)`,
    });
  const metricCards: StatCardProps[] = [
    {
      id: 'active-projects',
      label: t('home.stats.activeProjects'),
      value: formatInteger(activeProjectCount),
      icon: TrendingUp,
      loading: summaryQ.isLoading,
      search: { status: ACTIVE_PROJECT_STATUSES },
      disabled: !canListProjects,
    },
    {
      id: 'upcoming-deadlines',
      label: t('home.stats.upcomingDeadlines'),
      value: formatInteger(upcomingDeadlinesQ.data?.total ?? 0),
      icon: TrendingUp,
      loading: upcomingDeadlinesQ.isLoading,
      search: { upcomingDeadline: true },
      disabled: !canListProjects,
    },
    {
      id: 'completedThisMonth',
      label: t('home.stats.completedThisMonth'),
      value: formatInteger(completedThisMonthQ.data?.total ?? 0),
      icon: TrendingUp,
      loading: completedThisMonthQ.isLoading,
      search: { status: ['ACCEPTED'], year: [currentYear], month: [currentMonth] },
      disabled: !canListProjects,
    },
    {
      id: 'accepted-offers',
      label: t('home.stats.acceptedOffers'),
      value: formatInteger(summary?.projectsByStatus.ACCEPTED ?? 0),
      icon: TrendingUp,
      loading: summaryQ.isLoading,
      search: { status: ['ACCEPTED'] },
      disabled: !canListProjects,
    },
  ];
  const recentColumns: RecentProjectColumn[] = [
    {
      key: 'title',
      label: t('home.recent.columns.title'),
      headerClassName: 'px-5 py-4 font-semibold',
      className: 'px-5 py-4',
      render: (p) => (
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-primary" />
          <span className="font-medium">{p.title}</span>
        </div>
      ),
    },
    {
      key: 'client',
      label: t('home.recent.columns.client'),
      headerClassName: 'px-5 py-4 font-semibold',
      className: 'px-5 py-4 text-muted-foreground',
      render: (p) => p.clientName,
    },
    {
      key: 'rooms',
      label: t('home.recent.columns.rooms'),
      headerClassName: 'px-5 py-4 font-semibold',
      className: 'px-5 py-4 text-muted-foreground',
      render: (p) => roomCountLabel(p.roomCount ?? 0),
    },
    {
      key: 'agent',
      label: t('home.recent.columns.agent'),
      headerClassName: 'px-5 py-4 font-semibold',
      className: 'px-5 py-4 text-muted-foreground',
      render: (p) => (p.agentId ? (userNameById.get(p.agentId) ?? '—') : '—'),
    },
    {
      key: 'status',
      label: t('home.recent.columns.status'),
      headerClassName: 'px-5 py-4 font-semibold',
      className: 'px-5 py-4',
      render: (p) => (
        <span
          className={
            'inline-flex items-center rounded-xl px-2.5 py-1 text-sm font-medium ' +
            projectStatusBadgeClass(p.status)
          }
        >
          {projectStatusLabel(t, p.status, p.reofferCount)}
        </span>
      ),
    },
    {
      key: 'deadline',
      label: t('home.recent.columns.deadline'),
      headerClassName: 'px-5 py-4 font-semibold',
      className: 'px-5 py-4 text-muted-foreground',
      render: (p) => formatDateOnly(p.deadline),
    },
  ];
  const { orderedColumns, moveColumn, resetColumnOrder } = usePersistedColumnOrder(
    'home.recent-projects',
    recentColumns,
  );

  return (
    <div className="space-y-7">
      {(canCreateClients || canCreateProjects) && (
        <section>
          <h2 className="mb-4 text-xl font-medium">{t('home.quickActions.title')}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {canCreateClients && (
              <Link
                to="/clients/new"
                search={{}}
                className="flex h-14 items-center gap-3 rounded-2xl bg-primary px-5 text-lg font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <UserPlus className="h-5 w-5" />
                <span>{t('home.quickActions.newClient')}</span>
              </Link>
            )}
            {canCreateProjects && (
              <Link
                to="/projects/new"
                search={{}}
                className="flex h-14 items-center gap-3 rounded-2xl bg-muted px-5 text-lg font-medium text-foreground transition-colors hover:bg-muted/80"
              >
                <FolderPlus className="h-5 w-5" />
                <span>{t('home.quickActions.newProject')}</span>
              </Link>
            )}
          </div>
        </section>
      )}

      <section>
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-xl font-medium">{t('home.stats.title')}</h2>
          <RangeSelect value={rangeDays} onChange={setRangeDays} />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryQ.isError ? (
            <div className="md:col-span-2 xl:col-span-4">
              <ErrorState error={summaryQ.error} onRetry={() => void summaryQ.refetch()} />
            </div>
          ) : (
            metricCards.map((card) => <StatCard key={card.id} {...card} />)
          )}
        </div>
      </section>

      {canListProjects && (
        <section>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-medium">{t('home.recent.title')}</h2>
            <ColumnOrderMenu
              columns={orderedColumns}
              onMoveColumn={moveColumn}
              onReset={resetColumnOrder}
            />
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead className="bg-muted/40 text-left text-foreground">
                  <tr>
                    {orderedColumns.map((column) => (
                      <th key={column.key} className={column.headerClassName}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentQ.isError && (
                    <tr>
                      <td colSpan={orderedColumns.length} className="px-5 py-6">
                        <ErrorState error={recentQ.error} onRetry={() => void recentQ.refetch()} />
                      </td>
                    </tr>
                  )}
                  {!recentQ.isError && recentQ.isLoading && (
                    <tr>
                      <td
                        colSpan={orderedColumns.length}
                        className="px-5 py-8 text-center text-muted-foreground"
                      >
                        {t('home.recent.loading')}
                      </td>
                    </tr>
                  )}
                  {!recentQ.isError && !recentQ.isLoading && recent.length === 0 && (
                    <tr>
                      <td colSpan={orderedColumns.length} className="px-5 py-6">
                        <EmptyState description={t('home.recent.empty')} />
                      </td>
                    </tr>
                  )}
                  {recent.map((p) => (
                    <tr
                      key={p.id}
                      {...clickableRowProps(() =>
                        navigate({ to: '/projects/$id', params: { id: p.id } }),
                      )}
                      className="cursor-pointer border-t border-border transition-colors hover:bg-muted/30"
                    >
                      {orderedColumns.map((column) => (
                        <td key={column.key} className={column.className}>
                          {column.render(p)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, loading, search, disabled }: StatCardProps) {
  const { t } = useTranslation();
  const className =
    'block rounded-2xl border border-border bg-card p-5 text-left shadow-sm transition ' +
    (disabled
      ? ''
      : 'hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring');
  const content = (
    <>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
        <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
      </div>
      <div className="text-base text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-end gap-3">
        <span className="text-4xl font-semibold leading-none text-foreground">
          {loading ? '—' : value}
        </span>
      </div>
    </>
  );

  if (disabled || !search) {
    return <article className={className}>{content}</article>;
  }

  return (
    <Link
      to="/projects"
      search={search}
      className={className}
      aria-label={t('home.stats.openFilter', { label })}
    >
      {content}
    </Link>
  );
}

function RangeSelect({
  value,
  onChange,
}: {
  value: DashboardRangeDays;
  onChange: (value: DashboardRangeDays) => void;
}) {
  const { t } = useTranslation();

  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">{t('home.stats.range.label')}</span>
      <select
        value={value}
        onChange={(event) => onChange(Number(event.target.value) as DashboardRangeDays)}
        className="h-10 appearance-none rounded-2xl border border-border bg-card py-0 pl-4 pr-10 text-base font-medium text-foreground shadow-sm outline-none transition-colors hover:bg-muted/40 focus:border-primary focus:ring-2 focus:ring-primary/20"
      >
        {RANGE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {t(`home.stats.range.${option}`)}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-foreground" />
    </label>
  );
}

function formatDateOnly(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '—';
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat('ro-RO').format(value);
}
